import { useAccPipelineStore } from "../stores/accPipelineStore";

/** Full-viewer overlay with spinner + realistic pipeline progress. */
export function AccAnalysisOverlay() {
  const active = useAccPipelineStore(s => s.active);
  const phase = useAccPipelineStore(s => s.phase);
  const percent = useAccPipelineStore(s => s.percent);
  const message = useAccPipelineStore(s => s.message);
  const warning = useAccPipelineStore(s => s.warning);

  if (phase === "error") {
    return (
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "rgba(8, 10, 14, 0.92)",
          zIndex: 20,
        }}
      >
        <div
          style={{
            maxWidth: 400,
            padding: 16,
            borderRadius: 8,
            border: "1px solid rgba(248,113,113,0.4)",
            background: "rgba(248,113,113,0.08)",
            color: "#fca5a5",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ display: "block", marginBottom: 6 }}>
            Could not load model
          </strong>
          {message}
        </div>
      </div>
    );
  }

  if (!active) {
    if (phase === "ready" && warning) {
      return (
        <div
          style={{
            position: "absolute",
            left: 12,
            right: 12,
            bottom: 12,
            zIndex: 15,
            padding: "10px 12px",
            borderRadius: 6,
            background: "rgba(229,184,95,0.12)",
            border: "1px solid rgba(229,184,95,0.4)",
            color: "#e5b85f",
            fontSize: 12,
            lineHeight: 1.45,
            pointerEvents: "none",
          }}
        >
          {warning}
        </div>
      );
    }
    return null;
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        background: "rgba(8, 10, 14, 0.88)",
        zIndex: 20,
        pointerEvents: "none",
      }}
    >
      <div
        aria-hidden
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          border: "3px solid rgba(127,179,213,0.2)",
          borderTopColor: "#7fb3d5",
          animation: "vera-spin 0.9s linear infinite",
        }}
      />
      <style>
        {`@keyframes vera-spin { to { transform: rotate(360deg); } }`}
      </style>
      <div style={{ textAlign: "center", maxWidth: 320, padding: "0 16px" }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--vera-text)",
            marginBottom: 6,
          }}
        >
          Analyzing model…
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--vera-text-dim)",
            lineHeight: 1.45,
            marginBottom: 12,
          }}
        >
          {message}
        </div>
        <div
          style={{
            width: 240,
            height: 6,
            borderRadius: 3,
            background: "rgba(255,255,255,0.08)",
            overflow: "hidden",
            margin: "0 auto 6px",
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: "100%",
              borderRadius: 3,
              background: "linear-gradient(90deg, #5b8fb9, #7fb3d5)",
              transition: "width 0.35s ease",
            }}
          />
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#7fb3d5",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {percent}%
        </div>
      </div>
    </div>
  );
}

/** Shown when APS auth is on but the user has not picked an .rvt yet. */
export function AccEmptyViewerPrompt() {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 32,
        textAlign: "center",
        background: "#111",
        zIndex: 10,
      }}
    >
      <div
        aria-hidden
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          border: "1px dashed rgba(127,179,213,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
          color: "rgba(127,179,213,0.5)",
        }}
      >
        📄
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--vera-text)" }}>
        No model selected
      </div>
      <p
        style={{
          margin: 0,
          maxWidth: 360,
          fontSize: 13,
          lineHeight: 1.5,
          color: "var(--vera-text-dim)",
        }}
      >
        Choose a Revit{" "}
        <strong style={{ color: "var(--vera-text)" }}>.rvt</strong> file from
        the project browser in the left panel to load and analyze it from ACC.
      </p>
    </div>
  );
}
